import './cartParticipantLine.scss';

import { useIntl } from 'react-intl';
import { Skeleton } from '@eliberty/design-system-react';

import { ClockIcon, FormattedCurrency } from '~atoms';
import type { ParticipantDisplay } from '~types/cart';

export interface CartParticipantLineProps {
  participant: ParticipantDisplay;
  currency: string;
  isPriceLoading?: boolean;
  onRemove: () => void;
}

const CloseIcon = () => (
  <svg
    aria-hidden
    fill='none'
    height='14'
    stroke='currentColor'
    strokeLinecap='round'
    strokeLinejoin='round'
    strokeWidth='2'
    viewBox='0 0 24 24'
    width='14'
  >
    <path d='M6 6l12 12M18 6L6 18' />
  </svg>
);

const CartParticipantLine = ({
  currency,
  isPriceLoading = false,
  onRemove,
  participant,
}: CartParticipantLineProps) => {
  const intl = useIntl();

  return (
    <div className='CartParticipantLine'>
      <div className='CartParticipantLine_main'>
        <div className='CartParticipantLine_row'>
          <span className='CartParticipantLine_type'>{participant.consumerCategoryLabel}</span>
          <span className='CartParticipantLine_price'>
            {isPriceLoading ? (
              <Skeleton height={18} width={56} />
            ) : (
              <FormattedCurrency currency={currency} value={participant.priceInCents} />
            )}
          </span>
        </div>
        {participant.options.length > 0 && (
          <ul className='CartParticipantLine_options'>
            {participant.options.map((option, index) => (
              <li className='CartParticipantLine_option' key={`${option.label}-${index}`}>
                <span className='CartParticipantLine_optionLabel'>
                  <span aria-hidden className='CartParticipantLine_optionBullet'>
                    +
                  </span>
                  <span className='CartParticipantLine_optionText'>
                    <span className='CartParticipantLine_optionName'>{option.label}</span>
                    {option.timeslotLabel && (
                      <span className='CartParticipantLine_optionTimeslot'>
                        <ClockIcon size={11} />
                        {option.timeslotLabel}
                      </span>
                    )}
                  </span>
                </span>
                <span className='CartParticipantLine_optionPrice'>
                  {isPriceLoading ? (
                    <Skeleton height={14} width={40} />
                  ) : (
                    <>
                      +<FormattedCurrency currency={currency} value={option.priceInCents} />
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        aria-label={intl.formatMessage({
          defaultMessage: 'Remove this participant',
          id: 'cartAriaRemoveParticipant',
        })}
        className='CartParticipantLine_remove'
        onClick={onRemove}
        type='button'
      >
        <CloseIcon />
      </button>
    </div>
  );
};

export default CartParticipantLine;
